import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import React, { lazy, Suspense, useEffect } from "react";

import { Layout } from "@/components/layout";
import { AuthProvider } from "@/contexts/auth-context";
import { useAuth } from "@/hooks/use-auth";
import { useSocket } from "@/hooks/use-socket";
import { FeedbackWidget } from "@/components/feedback-widget";
import { AnalyticsConsentBanner } from "@/components/analytics-consent";

const Login = lazy(() => import("@/pages/auth/login"));
const Register = lazy(() => import("@/pages/auth/register"));
const ForgotPassword = lazy(() => import("@/pages/auth/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/auth/reset-password"));
const VerifyEmail = lazy(() => import("@/pages/auth/verify-email"));
const Uitschrijven = lazy(() => import("@/pages/uitschrijven"));
const Voorwaarden = lazy(() => import("@/pages/voorwaarden"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Discover = lazy(() => import("@/pages/discover"));
const Matches = lazy(() => import("@/pages/matches"));
const Conversations = lazy(() => import("@/pages/conversations"));
const Chat = lazy(() => import("@/pages/chat"));
const ProfileView = lazy(() => import("@/pages/profile-view"));
const ProfileDetail = lazy(() => import("@/pages/profile-detail"));
const ProfileEdit = lazy(() => import("@/pages/profile-edit"));
const ProfileSettings = lazy(() => import("@/pages/profile-settings"));
const KinderwensScore = lazy(() => import("@/pages/kinderwens-score"));
const PartnerwensScore = lazy(() => import("@/pages/partnerwens-score"));
const Pricing = lazy(() => import("@/pages/pricing"));
const Upgraden = lazy(() => import("@/pages/upgraden"));
const ProfielBezoekers = lazy(() => import("@/pages/profielbezoekers"));
const WieLiketeMij = lazy(() => import("@/pages/wie-likete-mij"));
const ProfielAanmaken = lazy(() => import("@/pages/profiel-aanmaken"));
const AdminPage = lazy(() => import("@/pages/admin"));
const NotFound = lazy(() => import("@/pages/not-found"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function RouteLoading() {
  return (
    <div
      className="flex min-h-[60vh] w-full items-center justify-center px-4"
      role="status"
      aria-live="polite"
      aria-label="Pagina laden"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-background/80 px-5 py-4 text-sm text-muted-foreground shadow-sm">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary/25 border-t-primary" aria-hidden="true" />
        <span>Pagina laden…</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ component: Component, requireVerified: needsVerification = true, requireAdmin = false, ...rest }: { component: React.ComponentType<Record<string, unknown>>; requireVerified?: boolean; requireAdmin?: boolean; [key: string]: unknown }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const isAdminUser = !!user && (user.role === "admin" || user.isAdmin === true);
  const enforceVerification = needsVerification && !isAdminUser;

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        setLocation('/inloggen', { replace: true });
      } else if (requireAdmin && user && !isAdminUser) {
        setLocation('/ontdekken', { replace: true });
      } else if (enforceVerification && user && !user.isVerified) {
        setLocation('/email-verificatie?status=pending', { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, enforceVerification, requireAdmin, isAdminUser, user, setLocation]);

  if (isLoading) return <RouteLoading />;
  if (!isAuthenticated) return null;
  if (requireAdmin && user && !isAdminUser) return null;
  if (enforceVerification && user && !user.isVerified) return null;

  return <Component {...rest} />;
}

function SocketProvider({ children }: { children: React.ReactNode }) {
  useSocket();
  return <>{children}</>;
}

function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading) {
      const isAdmin = user?.role === "admin" || user?.isAdmin === true;
      if (!isAuthenticated) {
        setLocation('/inloggen', { replace: true });
      } else if (user && !user.isVerified && !isAdmin) {
        setLocation('/email-verificatie?status=pending', { replace: true });
      } else if (isAdmin) {
        setLocation('/admin', { replace: true });
      } else {
        setLocation('/ontdekken', { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, user, setLocation]);

  return null;
}

function Router() {
  return (
    <SocketProvider>
      <AnalyticsConsentBanner />
      <Layout>
        <Suspense fallback={<RouteLoading />}>
          <Switch>
            <Route path="/" component={RootRedirect} />
            <Route path="/inloggen" component={Login} />
            <Route path="/registreren" component={Register} />
            <Route path="/wachtwoord-vergeten" component={ForgotPassword} />
            <Route path="/wachtwoord-resetten" component={ResetPassword} />
            <Route path="/email-verificatie" component={VerifyEmail} />
            <Route path="/uitschrijven" component={Uitschrijven} />
            <Route path="/voorwaarden" component={Voorwaarden} />
            <Route path="/privacy" component={Privacy} />

          {/* Protected Routes */}
          <Route path="/ontdekken">
            {() => <ProtectedRoute component={Discover} />}
          </Route>
          <Route path="/matches">
            {() => <ProtectedRoute component={Matches} />}
          </Route>
          <Route path="/berichten">
            {() => <ProtectedRoute component={Conversations} />}
          </Route>
          <Route path="/berichten/:matchId">
            {() => <ProtectedRoute component={Chat} />}
          </Route>
          <Route path="/profiel/aanmaken">
            {() => <ProtectedRoute component={ProfielAanmaken} />}
          </Route>
          <Route path="/profiel/bewerken">
            {() => <ProtectedRoute component={ProfileEdit} />}
          </Route>
          <Route path="/profiel/instellingen">
            {() => <ProtectedRoute component={ProfileSettings} />}
          </Route>
          <Route path="/profiel">
            {() => <ProtectedRoute component={ProfileView} />}
          </Route>
          <Route path="/kinderwens-score">
            {() => <ProtectedRoute component={KinderwensScore} />}
          </Route>
          <Route path="/partnerwens-score">
            {() => <ProtectedRoute component={PartnerwensScore} />}
          </Route>
          <Route path="/prijzen" component={Pricing} />
          <Route path="/upgraden" component={Upgraden} />
          <Route path="/profielbezoekers">
            {() => <ProtectedRoute component={ProfielBezoekers} />}
          </Route>
          <Route path="/wie-likete-mij">
            {() => <ProtectedRoute component={WieLiketeMij} />}
          </Route>
          <Route path="/profiel/:id">
            {(params) => <ProtectedRoute component={ProfileDetail} userId={params.id} />}
          </Route>
          <Route path="/admin">
            {() => <ProtectedRoute component={AdminPage} requireVerified={false} requireAdmin />}
          </Route>

            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </Layout>
    </SocketProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <AuthProvider>
            <Router />
          </AuthProvider>
          <FeedbackWidget />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
